import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import { authApi } from '../api/auth';
import { useAuth } from '@/lib/auth/AuthContext';
import type { LoginCredentials } from '../types';

/**
 * Custom React Query hook for handling AWS Cognito authentication.
 *
 * Invokes `authApi.loginCognito(credentials)`, updates the global AuthContext,
 * displays user feedback notifications via Sonner toast, and redirects to `/dashboard`.
 *
 * @returns React Query useMutation object.
 */
export function useLogin() {
  const { setAuthenticated, setUser } = useAuth();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => authApi.loginCognito(credentials),
    onSuccess: (data) => {
      if (data?.user) {
        setUser(data.user);
      }
      setAuthenticated(true);
      navigate('/dashboard');
      toast.success('Successfully logged in');
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      let message = 'Failed to login. Please check your credentials.';
      
      if (error.response) {
        // Server responded with a status code out of the 2xx range
        if (error.response.status >= 500) {
          message = `Server Error (${error.response.status}): The backend is experiencing issues. Check server logs.`;
        } else if (error.response.data && error.response.data.detail) {
          message = error.response.data.detail;
        } else if (error.response.status === 404) {
          message = 'Endpoint not found. Is the backend running the latest code?';
        }
      } else if (error.request) {
        // The request was made but no response was received (e.g. CORS, offline, or server crash)
        message = 'Network Error: Could not reach the backend server.';
      } else {
        // Something else happened
        message = error.message || message;
      }
      
      toast.error(message);
    }
  });
}
